import { STPageType, ShapeType, TargetType } from '@tencent/slide-sdk-pb-model';

export enum DataType {
    /** Presentation - 幻灯片总数据 */
    Presentation = 1,

    /** 页元素 */
    /** Slide - 普通幻灯片页 */
    Slide = 2,
    /** SlideMaster - 母版 */
    SlideMaster = 3,
    /** SlideLayout - 版式 */
    SlideLayout = 4,
    /** Shape - 备注 */
    Notes = 5,
    /** Shape - 母版备注 */
    NotesMaster = 6,

    /** 形状 */
    /** Shape - 普通形状 */
    Shape = 7,
    /** GroupShape - 组合 */
    GroupShape = 8,
    /** Connector - 连接线 */
    Connector = 9,
    /** Picture - 图片 */
    Picture = 10,
    /** GraphicalObjectFrameTable - 表格（继承自 GraphicObjectFrame） */
    GraphicalObjectFrameTable = 11,
    /** GraphicalObjectFrameChart - 图表（继承自 GraphicObjectFrame） */
    GraphicalObjectFrameChart = 12,
    /** GraphicalObjectFrameUnknown - 未细分的 GraphicObjectFrame，导入时 Keep 用 */
    GraphicalObjectFrameUnknown = 13,

    /** 其他 */
    /** Section - 节 */
    Section = 14,
    /** Theme - 主题 */
    Theme = 15,
    /** Theme - 动画 */
    Timing = 16,
    /** GraphicalObjectFrameSmartArt - SmartArt（继承自 GraphicObjectFrame） */
    GraphicalObjectFrameSmartArt = 17,
}

export class DataTypeUtils {
    public static fromSTPageType(pageType: Exclude<STPageType, STPageType.PBDEFAULT>) {
        return (
            {
                [STPageType.slide]: DataType.Slide,
                [STPageType.slideMaster]: DataType.SlideMaster,
                [STPageType.slideLayout]: DataType.SlideLayout,
                [STPageType.notes]: DataType.Notes,
                [STPageType.notesMaster]: DataType.NotesMaster,
            } as const
        )[pageType];
    }

    public static fromShapeType(shapeType: Exclude<ShapeType, ShapeType.Unknown>) {
        if (shapeType === ShapeType.GraphicalObjectFrameUnknown) {
            throw new Error(`unknown shape type ${ShapeType[shapeType]}`);
        }

        return (
            {
                [ShapeType.Shape]: DataType.Shape,
                [ShapeType.Equation]: DataType.Shape,
                [ShapeType.GroupShape]: DataType.GroupShape,
                [ShapeType.GraphicalObjectFrameSmartart]: DataType.GraphicalObjectFrameSmartArt,
                [ShapeType.GraphicalObjectFrameSmartartNew]: DataType.GraphicalObjectFrameSmartArt,
                [ShapeType.Picture]: DataType.Picture,
                [ShapeType.Connector]: DataType.Connector,
                [ShapeType.GraphicalObjectFrameTable]: DataType.GraphicalObjectFrameTable,
                [ShapeType.GraphicalObjectFrameChart]: DataType.GraphicalObjectFrameChart,
                [ShapeType.Ink]: DataType.Picture,
                [ShapeType.GraphicalObjectFrameOleObject]: DataType.Picture,
                [ShapeType.GraphicalObjectFrameThreeDimentionalModel]: DataType.Picture,
            } as const
        )[shapeType];
    }

    public static toSTPageType(pageDataType: PageDataType) {
        return (
            {
                [DataType.Slide]: STPageType.slide,
                [DataType.SlideMaster]: STPageType.slideMaster,
                [DataType.SlideLayout]: STPageType.slideLayout,
                [DataType.Notes]: STPageType.notes,
                [DataType.NotesMaster]: STPageType.notesMaster,
            } as const
        )[pageDataType];
    }

    public static toShapeType(shapeDataType: ShapeDataType) {
        return (
            {
                [DataType.Shape]: ShapeType.Shape,
                [DataType.GroupShape]: ShapeType.GroupShape,
                [DataType.Picture]: ShapeType.Picture,
                [DataType.Connector]: ShapeType.Connector,
                [DataType.GraphicalObjectFrameTable]: ShapeType.GraphicalObjectFrameTable,
                [DataType.GraphicalObjectFrameChart]: ShapeType.GraphicalObjectFrameChart,
                [DataType.GraphicalObjectFrameSmartArt]: ShapeType.GraphicalObjectFrameSmartartNew,
                [DataType.GraphicalObjectFrameUnknown]: ShapeType.GraphicalObjectFrameUnknown,
            } as const
        )[shapeDataType];
    }

    public static toTargetType(shapeDataType: DataType) {
        return (
            {
                [DataType.Presentation]: TargetType.Presentation,
                [DataType.Theme]: TargetType.Theme,
                [DataType.Slide]: TargetType.Page,
                [DataType.SlideLayout]: TargetType.Page,
                [DataType.SlideMaster]: TargetType.Page,
                [DataType.Notes]: TargetType.Unknown,
                [DataType.NotesMaster]: TargetType.Unknown,
                [DataType.Section]: TargetType.Unknown,
                [DataType.Timing]: TargetType.Unknown,
                [DataType.Shape]: TargetType.Shape,
                [DataType.GroupShape]: TargetType.Shape,
                [DataType.Picture]: TargetType.Shape,
                [DataType.Connector]: TargetType.Shape,
                [DataType.GraphicalObjectFrameTable]: TargetType.Shape,
                [DataType.GraphicalObjectFrameChart]: TargetType.Shape,
                [DataType.GraphicalObjectFrameSmartArt]: TargetType.Shape,
                [DataType.GraphicalObjectFrameUnknown]: TargetType.Shape,
            } as const
        )[shapeDataType];
    }
}

export type ShapeDataType =
    | DataType.Shape
    | DataType.GroupShape
    | DataType.Picture
    | DataType.Connector
    | DataType.GraphicalObjectFrameTable
    | DataType.GraphicalObjectFrameChart
    | DataType.GraphicalObjectFrameSmartArt
    | DataType.GraphicalObjectFrameUnknown;

export const SHAPE_DATA_TYPES = [
    DataType.Shape,
    DataType.GroupShape,
    DataType.Picture,
    DataType.Connector,
    DataType.GraphicalObjectFrameTable,
    DataType.GraphicalObjectFrameChart,
    DataType.GraphicalObjectFrameSmartArt,
    DataType.GraphicalObjectFrameUnknown,
] as const;

export type PageDataType =
    | DataType.Slide
    | DataType.SlideMaster
    | DataType.SlideLayout
    | DataType.Notes
    | DataType.NotesMaster;

export const PAGE_DATA_TYPES = [
    DataType.Slide,
    DataType.SlideMaster,
    DataType.SlideLayout,
    DataType.Notes,
    DataType.NotesMaster,
] as const;

export const SLIDE_PAGE_DATA_TYPES = [
    DataType.Slide,
    DataType.SlideMaster,
    DataType.SlideLayout,
    DataType.Notes,
    DataType.NotesMaster,
] as const;

export interface IData<Type extends DataType = DataType> {
    type: Type;
    id: string;
}

export type WithExtensions<Keys extends string> = {
    extensions?: IExtensionData<Keys>;
};

/*
 * 业务拓展数据，用于存储业务相关的数据，如：AIGC 的原始文本、模版ID等
 * - 一般情况下不参与导入导出，仅在数据中台和前端中流通
 * - 数据对象销毁时，自动删除；撤销时，自动恢复
 */
export type IExtensionData<E extends string> = Partial<{
    [K in E]?: string;
}>;
